/**
 * Unified Logger Utility
 * Consolidated logging functionality for the LoveGroomers theme
 *
 * Features:
 * - Namespaced logging with prefixes
 * - Conditional activation via URL params or localStorage
 * - Log level filtering (debug, info, warn, error)
 * - Performance timing with timeStart/timeEnd
 * - Multiple log levels (debug, info, warn, error)
 * - Console grouping support
 * - Zero-overhead when disabled (no-op functions)
 * - Standalone safeParse utility
 *
 * Usage:
 * ```javascript
 * import createLogger, { safeParse } from './common/utils/logger';
 *
 * // Basic usage
 * const log = createLogger('MyComponent');
 * log.info('Component initialized');
 *
 * // With call ID for tracking
 * const log = createLogger('ProductPage', generateUUID());
 * log.debug('Loading product data', productData);
 *
 * // Performance timing (new!)
 * log.timeStart('data-fetch');
 * await fetchData();
 * log.timeEnd('data-fetch'); // Shows elapsed time
 *
 * // Console grouping
 * log.group('Processing items');
 * items.forEach(item => log.debug('Item:', item));
 * log.groupEnd();
 *
 * // Standalone safeParse (always works!)
 * const data = safeParse(localStorage.getItem('myData'));
 * ```
 *
 * Activation:
 * - Add `?debug=true` to URL for all logging
 * - Add `?debug=true&logLevel=warn` to URL to show only warnings and errors
 * - Add `?debug=ssw` to URL for SSW-specific logging (backward compatible)
 * - Set `localStorage.setItem('debugLogging', 'true')` for persistent logging
 * - Set `localStorage.setItem('logLevel', 'warn')` to filter by level
 */

/**
 * Log level constants
 */
const LOG_LEVELS = {
    debug: 0,
    info: 1,
    log: 2,
    warn: 3,
    error: 4,
    silent: 5
};

/**
 * Generate a unique identifier for call tracking
 * @returns {string} UUID v4 string
 */
export function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Check if logging is enabled
 * Checks both URL parameters and localStorage for debug flags
 * @returns {boolean} True if logging is enabled
 */
function isLoggingEnabled() {
    // No window (node/SSR or unit-test env) → no console to gate; stay disabled.
    if (typeof window === 'undefined') return false;
    const urlParams = window.location.search;
    const fromUrl = urlParams.includes('debug=true') || urlParams.includes('debug=ssw');
    const fromStorage = localStorage.getItem('debugLogging') === 'true' ||
                        localStorage.getItem('sswLogging') === 'true';
    return fromUrl || fromStorage;
}

/**
 * Get the current log level from URL or localStorage
 * @returns {string} Current log level (debug, info, log, warn, error, silent)
 */
function getLogLevel() {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlLevel = urlParams.get('logLevel');
        const storageLevel = localStorage.getItem('logLevel');
        const level = urlLevel || storageLevel || 'debug'; // default to show all

        // Validate level
        return LOG_LEVELS.hasOwnProperty(level) ? level : 'debug';
    } catch {
        return 'debug';
    }
}

/**
 * Check if a message at the given level should be logged
 * @param {string} messageLevel - The level of the message (debug, info, warn, error)
 * @returns {boolean} True if the message should be logged
 */
function shouldLog(messageLevel) {
    const currentLevel = LOG_LEVELS[getLogLevel()];
    const msgLevel = LOG_LEVELS[messageLevel];
    return msgLevel >= currentLevel;
}

/**
 * Standalone safe JSON parser that always works (even when logger is disabled)
 * Use this for critical JSON parsing operations
 *
 * @param {string|object} value - Value to parse
 * @param {string} [label='Data'] - Label for logging context (only used if logging enabled)
 * @returns {object|null} Parsed object or null on error
 *
 * @example
 * import { safeParse } from './common/utils/logger';
 * const data = safeParse(localStorage.getItem('myData'));
 */
export function safeParse(value, label = 'Data') {
    // Handle null, undefined, or empty string
    if (value == null || value === '') {
        return null;
    }

    // If already an object, return it
    if (typeof value === 'object') {
        return value;
    }

    // Must be a string to parse
    if (typeof value !== 'string') {
        return null;
    }

    // Attempt to parse JSON
    try {
        return JSON.parse(value);
    } catch (err) {
        return null;
    }
}

/**
 * Format time duration for display
 * @param {number} ms - Time in milliseconds
 * @returns {string} Formatted time string
 */
function formatTime(ms) {
    if (ms < 1000) {
        return `${ms.toFixed(2)}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Create a namespaced logger instance
 * @param {string} namespace - Component or module name
 * @param {string} [callId] - Optional unique identifier for this logger instance
 * @returns {Logger} Logger instance
 */
export default function createLogger(namespace, callId) {
    const prefix = callId ? `[${namespace}#${callId}]` : `[${namespace}]`;
    const t0 = performance?.now?.() ?? Date.now();
    const isEnabled = isLoggingEnabled();

    // Return no-op logger if disabled (zero overhead)
    if (!isEnabled) {
        const noop = () => {};
        const noopWithReturn = () => null;
        return {
            prefix,
            namespace,
            callId,
            isEnabled: false,
            debug: noop,
            info: noop,
            log: noop,
            warn: noop,
            error: noop,
            group: noop,
            groupCollapsed: noop,
            groupEnd: noop,
            pmark: noop,
            timeStart: noop,
            timeEnd: noopWithReturn,
            table: noop,
            safeParse: (value) => safeParse(value), // Use standalone version
        };
    }

    let lastMarkTime = t0;
    const timings = new Map();

    /**
     * Performance mark with timing
     * Logs a message with elapsed time since logger creation or last pmark
     * @param {string} message - Message to log
     */
    function pmark(message) {
        const now = performance?.now?.() ?? Date.now();
        const elapsed = now - lastMarkTime;
        const total = now - t0;
        console.log(
            `${prefix} ⏱️  ${message}`,
            `[+${formatTime(elapsed)} | ${formatTime(total)} total]`
        );
        lastMarkTime = now;
    }

    /**
     * Start a named timer (better than console.time)
     * @param {string} label - Timer label
     */
    function timeStart(label) {
        timings.set(label, performance?.now?.() ?? Date.now());
        if (shouldLog('debug')) {
            console.debug(`${prefix} ⏱️  ${label} - started`);
        }
    }

    /**
     * End a named timer and display elapsed time
     * @param {string} label - Timer label
     * @returns {number|null} Duration in milliseconds, or null if timer wasn't started
     */
    function timeEnd(label) {
        const start = timings.get(label);
        if (!start) {
            if (shouldLog('warn')) {
                console.warn(`${prefix} Timer "${label}" was never started`);
            }
            return null;
        }

        const duration = (performance?.now?.() ?? Date.now()) - start;
        if (shouldLog('info')) {
            console.info(`${prefix} ⏱️  ${label} - completed in ${formatTime(duration)}`);
        }
        timings.delete(label);
        return duration;
    }

    /**
     * Console group with namespace prefix
     * @param {string} label - Group label
     * @param {boolean} [collapsed=false] - Whether to collapse the group
     */
    function group(label, collapsed = false) {
        const method = collapsed ? 'groupCollapsed' : 'group';
        console[method](`${prefix} ${label}`);
    }

    /**
     * Console groupCollapsed with namespace prefix
     * @param {string} label - Group label
     */
    function groupCollapsed(label) {
        console.groupCollapsed(`${prefix} ${label}`);
    }

    /**
     * End console group
     */
    function groupEnd() {
        console.groupEnd();
    }

    /**
     * Safe JSON parsing with error handling and logging (instance method)
     * @param {string|object} value - Value to parse
     * @param {string} [label='Data'] - Label for logging context
     * @returns {object|null} Parsed object or null on error
     */
    function safeParseWithLogging(value, label = 'Data') {
        // If already an object, return it
        if (typeof value === 'object' && value !== null) {
            if (shouldLog('warn')) {
                console.warn(`${prefix} ${label} received object instead of JSON string`);
            }
            return value;
        }

        // Check for invalid types
        if (typeof value !== 'string') {
            if (shouldLog('error')) {
                console.error(
                    `${prefix} ${label} received invalid type:`,
                    typeof value,
                    value
                );
            }
            return null;
        }

        // Attempt to parse JSON
        try {
            return JSON.parse(value);
        } catch (err) {
            if (shouldLog('error')) {
                console.error(`${prefix} ${label} failed to parse JSON:`, err);
            }
            if (shouldLog('debug')) {
                console.debug(`${prefix} Raw value:`, value);
            }
            return null;
        }
    }

    /**
     * Display data in a table format
     * @param {Array|Object} data - Data to display
     * @param {Array} [columns] - Optional column names
     */
    function table(data, columns) {
        console.log(`${prefix} Table:`);
        if (columns) {
            console.table(data, columns);
        } else {
            console.table(data);
        }
    }

    return {
        prefix,
        namespace,
        callId,
        isEnabled: true,

        // Standard log levels (with level filtering)
        debug: (...args) => {
            if (shouldLog('debug')) {
                console.debug(prefix, ...args);
            }
        },
        info: (...args) => {
            if (shouldLog('info')) {
                console.info(prefix, ...args);
            }
        },
        log: (...args) => {
            if (shouldLog('log')) {
                console.log(prefix, ...args);
            }
        },
        warn: (...args) => {
            if (shouldLog('warn')) {
                console.warn(prefix, ...args);
            }
        },
        error: (...args) => {
            if (shouldLog('error')) {
                console.error(prefix, ...args);
            }
        },

        // Console grouping
        group,
        groupCollapsed,
        groupEnd,

        // Performance tracking
        pmark,
        timeStart,
        timeEnd,

        // Utilities
        safeParse: safeParseWithLogging,
        table,
    };
}

// ---------------------------------------------------------------------------
// Remote logging (api/clientlog endpoint)
// ---------------------------------------------------------------------------

/** Module-level state set by initRemoteLogging() */
let _remoteLogUrl = null;
let _remoteInitialized = false;
let _remoteSending = false; // reentrancy guard for onerror → remote send cycle

/**
 * Resolve the clientlog URL from window.BC_CONTEXT (read at call-time, SPA-safe).
 * Mirrors the URL construction pattern in analytics-sender.js.
 * @returns {string|null}
 */
function _getRemoteLogUrl() {
    const ctx = window.BC_CONTEXT;
    if (!ctx) return null;
    if (ctx.site_environment === 'local') return 'https://localhost:7165/api/clientlog?key=Q9S7KYMT';
    const base = ctx.onlineServices || '';
    return base ? `${base.replace(/\/$/, '')}/products/api/clientlog?key=Q9S7KYMT` : null;
}

/**
 * Build the FrontendLogEntry payload.
 * @param {'Debug'|'Information'|'Warning'|'Error'} level
 * @param {string} namespace - logger namespace used as Source
 * @param {any[]} args - original console args; first string becomes Message
 */
function _buildRemotePayload(level, namespace, args) {
    const message = args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
    return {
        Level: level,
        Source: namespace,
        Message: message,
        Properties: {
            pageType: window.BC_CONTEXT?.pageType || '',
            pageUrl: window.location.href,
            userAgent: navigator.userAgent,
        },
    };
}

// Remote logging disabled for now — restore function + call sites below when re-enabling.
// /**
//  * Fire-and-forget POST to the remote log endpoint.
//  * Warning/Error use sendBeacon (survives page unload); Debug/Information use fetch.
//  * The backend api/clientlog endpoint must be exempt from CSRF validation because
//  * sendBeacon cannot carry custom request headers.
//  * @param {'Debug'|'Information'|'Warning'|'Error'} level
//  * @param {string} namespace
//  * @param {any[]} args
//  */
// function _sendRemote(level, namespace, args) {
//     const url = _remoteLogUrl || _getRemoteLogUrl();
//     if (!url) return;
//
//     const payload = _buildRemotePayload(level, namespace, args);
//     const useBeacon = level === 'Warning' || level === 'Error';
//
//     fetch(url, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload),
//     }).catch(() => { /* fire-and-forget */ });
// }

/**
 * Initialize remote logging.
 * Resolves the api/clientlog endpoint from window.BC_CONTEXT and registers
 * window.onerror + unhandledrejection handlers to capture uncaught errors.
 *
 * Call once from global.js onReady(). Idempotent.
 */
/**
 * Send a test event at every log level to verify the backend endpoint is reachable.
 * Exposed as window.testRemoteLogging() for quick browser-console testing.
 *
 * @example
 *   window.testRemoteLogging()
 *   window.testRemoteLogging('MyCustomMessage')
 */
export function testRemoteLogging(label = 'remote-logging-test') {
    const url = _remoteLogUrl || _getRemoteLogUrl();
    if (!url) {
        console.warn('[RemoteLogging] No endpoint URL available — has initRemoteLogging() been called?');
        return;
    }
    console.info(`[RemoteLogging] Sending test events to: ${url}`);
    const levels = ['Error','Debug', 'Information', 'Warning'];
    levels.forEach(level => {
        // _sendRemote(level, 'testRemoteLogging', [`${label} (${level})`]);
        console.info(`[RemoteLogging] Sent ${level} test event`);
    });
}

export function initRemoteLogging() {
    if (_remoteInitialized) return;
    _remoteInitialized = true;

    _remoteLogUrl = _getRemoteLogUrl();

    const previousOnError = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
        if (!_remoteSending) {
            _remoteSending = true;
            try {
                // _sendRemote('Error', 'window.onerror', [{
                //     message: String(message || 'Uncaught error'),
                //     scriptSource: source || '',
                //     line: lineno || 0,
                //     column: colno || 0,
                //     errorName: error?.name || '',
                //     errorStack: error?.stack?.substring(0, 1000) || '',
                // }]);
            } finally {
                _remoteSending = false;
            }
        }
        if (typeof previousOnError === 'function') return previousOnError.apply(this, arguments);
        return false;
    };

    window.addEventListener('unhandledrejection', function (event) {
        if (_remoteSending) return;
        _remoteSending = true;
        try {
            const reason = event.reason;
            const msg = reason instanceof Error ? reason.message
                : (typeof reason === 'string' ? reason : 'Unhandled Promise rejection');
            // _sendRemote('Error', 'window.unhandledrejection', [{
            //     message: msg,
            //     errorName: reason instanceof Error ? reason.name : '',
            //     errorStack: reason instanceof Error ? reason.stack?.substring(0, 1000) || '' : '',
            // }]);
        } finally {
            _remoteSending = false;
        }
    });
}

/**
 * Create a logger that's always enabled (ignores debug flags)
 * Useful for critical error logging that should always appear
 * @param {string} namespace - Component or module name
 * @param {string} [callId] - Optional unique identifier
 * @returns {Logger} Always-enabled logger instance
 */
export function createAlwaysLogger(namespace, callId) {
    const prefix = callId ? `[${namespace}#${callId}]` : `[${namespace}]`;
    const t0 = performance?.now?.() ?? Date.now();
    let lastMarkTime = t0;
    const timings = new Map();

    function pmark(message) {
        const now = performance?.now?.() ?? Date.now();
        const elapsed = now - lastMarkTime;
        const total = now - t0;
        console.log(
            `${prefix} ⏱️  ${message}`,
            `[+${formatTime(elapsed)} | ${formatTime(total)} total]`
        );
        lastMarkTime = now;
    }

    function timeStart(label) {
        timings.set(label, performance?.now?.() ?? Date.now());
        console.debug(`${prefix} ⏱️  ${label} - started`);
    }

    function timeEnd(label) {
        const start = timings.get(label);
        if (!start) {
            console.warn(`${prefix} Timer "${label}" was never started`);
            return null;
        }

        const duration = (performance?.now?.() ?? Date.now()) - start;
        console.info(`${prefix} ⏱️  ${label} - completed in ${formatTime(duration)}`);
        timings.delete(label);
        return duration;
    }

    function group(label, collapsed = false) {
        const method = collapsed ? 'groupCollapsed' : 'group';
        console[method](`${prefix} ${label}`);
    }

    function groupCollapsed(label) {
        console.groupCollapsed(`${prefix} ${label}`);
    }

    function safeParseWithLogging(value, label = 'Data') {
        if (typeof value === 'object' && value !== null) {
            console.warn(`${prefix} ${label} received object instead of JSON string`);
            return value;
        }

        if (typeof value !== 'string') {
            console.error(
                `${prefix} ${label} received invalid type:`,
                typeof value,
                value
            );
            return null;
        }

        try {
            return JSON.parse(value);
        } catch (err) {
            console.error(`${prefix} ${label} failed to parse JSON:`, err);
            console.debug(`${prefix} Raw value:`, value);
            return null;
        }
    }

    function table(data, columns) {
        console.log(`${prefix} Table:`);
        if (columns) {
            console.table(data, columns);
        } else {
            console.table(data);
        }
    }

    return {
        prefix,
        namespace,
        callId,
        isEnabled: true,
        debug: (...args) => console.debug(prefix, ...args),
        info: (...args) => console.info(prefix, ...args),
        log: (...args) => console.log(prefix, ...args),
        warn: (...args) => { console.warn(prefix, ...args); /* if (_remoteInitialized) _sendRemote('Warning', namespace, args); */ },
        error: (...args) => { console.error(prefix, ...args); /* if (_remoteInitialized) _sendRemote('Error', namespace, args); */ },
        group,
        groupCollapsed,
        groupEnd: () => console.groupEnd(),
        pmark,
        timeStart,
        timeEnd,
        safeParse: safeParseWithLogging,
        table,
    };
}
